import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import ScanSuccessBip from "../sounds/scannerBips.mp3";
import "./BAR_QR_CODE_SCANNER.css";
import CameraIcon from "@mui/icons-material/Camera";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import IsLoading from "../../../../../IsLoading";

export default function BAR_QR_CODE_SCANNER({ onScan, reveal, setReveal }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const scannedRef = useRef(false);
  const [loading, setLoading] = useState(true);

  // Keep onScan fresh without triggering useEffect re-runs
  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");

  useEffect(() => {
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    let isMounted = true;

    const initDevices = async () => {
      try {
        setLoading(true);
        await navigator.mediaDevices.getUserMedia({ video: true });
        const videoDevices =
          await BrowserMultiFormatReader.listVideoInputDevices();

        if (isMounted) {
          setDevices(videoDevices);
          if (videoDevices.length > 0) {
            setDeviceId(videoDevices[0].deviceId);
          } else {
            setLoading(false);
          }
        }
      } catch (err) {
        console.error("Device initialization error:", err?.code || "REQUEST_FAILED");
        if (isMounted) setLoading(false);
      }
    };

    initDevices();

    return () => {
      isMounted = false;
      if (controlsRef.current) {
        controlsRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (!deviceId) return;

    scannedRef.current = false;
    setLoading(true);

    if (controlsRef.current) {
      controlsRef.current.stop();
      controlsRef.current = null;
    }

    let activeControls = null;

    const start = async () => {
      try {
        if (!videoRef.current) return;

        // 1. decodeFromVideoDevice resolves when camera stream starts
        activeControls = await readerRef.current.decodeFromVideoDevice(
          deviceId,
          videoRef.current,
          (result) => {
            if (!result || scannedRef.current) return;

            scannedRef.current = true;

            if (activeControls) {
              activeControls.stop();
            }
            if (controlsRef.current) {
              controlsRef.current.stop();
              controlsRef.current = null;
            }

            const stream = videoRef.current?.srcObject;
            if (stream) {
              stream.getTracks().forEach((track) => track.stop());
              videoRef.current.srcObject = null;
            }

            onScanRef.current?.(result.getText());
            if (reveal !== "cam-scanner-open") {
              handleClose();
            }
            setTimeout(() => {
              handleClose();
            }, 30000);
          },
        );

        controlsRef.current = activeControls;

        // 2. Direct state update after camera stream connects successfully
        setLoading(false);
      } catch (err) {
        console.error("Scanning error:", err?.code || "REQUEST_FAILED");
        setLoading(false);
      }
    };

    start();

    return () => {
      if (controlsRef.current) {
        controlsRef.current.stop();
        controlsRef.current = null;
      }
      if (activeControls) {
        activeControls.stop();
      }
    };
  }, [deviceId]);

  const handleClose = () => {
    scannedRef.current = true;

    if (controlsRef.current) {
      controlsRef.current.stop();
      controlsRef.current = null;
    }

    const stream = videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }

    setReveal("");
  };

  return (
    <div id="camScannerContainerWraps">
      {/* Ensure video stays in DOM while loading so ref exists */}
      <div
        id="camScannerContainer"
        style={{ display: loading ? "none" : "block" }}
        onClick={(e) => e.stopPropagation()}
      >
        <video
          ref={videoRef}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />

        <div className="camScanner-overlay fx-cl space2">
          <div className="fs1 fw600" style={{ color: "#ffd500" }}>
            Focus on the code
          </div>

          <div className="camScanner-frame">
            <div className="camScanner-grid"></div>
            <div className="camScanner-line"></div>
            <div className="corner top-left"></div>
            <div className="corner top-right"></div>
            <div className="corner bottom-left"></div>
            <div className="corner bottom-right"></div>
          </div>

          <div className="camScanner-bottom fx-cl spacem">
            <select
              style={{ color: "#fff" }}
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              disabled={devices.length === 0}
              className="camera-select"
            >
              {devices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera`}
                </option>
              ))}
            </select>
            <div className="fx-cl space1">
              <figure className="camScannerTags fx-ac">
                <span>Qr-Barcode</span>
                <span>Products</span>
                <span>Service</span>
              </figure>
              <div className="camScannerBtnCont g g3">
                <figure className="fx-cl fx-ac">
                  <OpenWithIcon fontSize="large" />
                  <span>Expand</span>
                </figure>
                <figure className="fx-cl fx-ac">
                  <QrCode2Icon fontSize="large" />
                  <span>Qr code</span>
                </figure>
                <figure className="fx-cl fx-ac" onClick={handleClose}>
                  <DoDisturbIcon fontSize="large" />
                  <span>Close Scanner</span>
                </figure>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div id="camScannerLoader">

        <div  className="CAMSCANNER_LOADING_SPINNER">&nbsp;</div>
        </div> 
      )}
    </div>
  );
}
